import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import api from "../api";
import { useShouldShowStaffView } from "../components/StaffViewToggle/useShouldShowStaffView";
import urls from "../urls";
import type {
  FeatureFlags,
  FeatureFlagsResponse
} from "./useFeatureFlags.types";
import { Flag } from "./useFeatureFlags.types";

/** Retrieves feature flags from Django Waffle API. Plain text flag keys are automatically camelized by api.ts.
 * Flags that are enabled for staff only in the Django admin (staff/superusers, nobody else) are only active while the staff view is shown. */
export function useFeatureFlags() {
  const { shouldShowStaffView } = useShouldShowStaffView();
  const { data, isLoading, error } = useQuery({
    queryKey: ["featureFlags"],
    queryFn: () => fetchFeatureFlags(),
    initialData: { flags: getInitialFalseFlags(), enabledForStaffOnly: [] },
    refetchOnWindowFocus: true
  });

  const featureFlags = useMemo(() => {
    if (shouldShowStaffView) {
      return data.flags;
    }

    const hiddenFlags = data.enabledForStaffOnly.map((flag) => [flag, false]);
    return { ...data.flags, ...Object.fromEntries(hiddenFlags) };
  }, [data, shouldShowStaffView]);

  return { featureFlags, isLoading, error };
}

async function fetchFeatureFlags() {
  const response = await api.get<FeatureFlagsResponse>(urls.api.featureFlags());
  return response.data;
}

function getInitialFalseFlags() {
  return Object.fromEntries(
    Object.values(Flag).map((flagValue) => [flagValue, false])
  ) as FeatureFlags;
}
